/**
 * Auth0 Utilities
 *
 * Helper functions for working with Auth0 in the application.
 * These utilities bridge the Auth0 React SDK with parts of the application
 * (like API interceptors) that need token access.
 */

import {
  SESSION_INVALID_ERROR_CODES,
  USER_NOT_FOUND_FLAG,
  BLOCKED_USER_ERROR_CODES,
  BLOCKED_USER_FLAG,
} from './constants';
// Reference to the getAccessTokenSilently function
let getAccessTokenSilentlyRef = null;
let logoutRef = null;

// Cached refresh promise to deduplicate concurrent refresh requests
let refreshPromise = null;

/**
 * Register the getAccessTokenSilently and logout functions
 * @param {function} getAccessTokenSilently - The getAccessTokenSilently function
 * @param {function} logout - The logout function
 */
export const registerAuth0TokenGetter = (getAccessTokenSilently, logout = null) => {
  getAccessTokenSilentlyRef = getAccessTokenSilently;
  logoutRef = logout;
};

/**
 * Check if the localStorage key belongs to Auth0.
 * @param {string} key
 * @returns {boolean}
 */
const isAuth0StorageKey = (key) => {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes('auth0') ||
    lowerKey.includes('@@auth0spajs@@') ||
    lowerKey.includes('a0.spajs') ||
    lowerKey === 'access_token'
  );
};

/**
 * Remove cached tokens and Auth0 session data from storage, preserving specific keys if needed.
 * @param {Set<string>} preserveKeys
 */
const clearAuth0Storage = (preserveKeys = new Set()) => {
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || preserveKeys.has(key)) continue;

    if (isAuth0StorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Normalize an Auth0 error object into a lowercase string for comparison.
 * @param {any} error
 * @returns {string}
 */
const normalizeAuth0Error = (error) => {
  if (!error) return '';

  const values = [
    error.error,
    error.error_description,
    error.errorDescription,
    error.message,
    error.code,
    error.errorCode,
    error.description,
    error.body,
  ];

  return values
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');
};

/**
 * Determine whether the error indicates that the session refresh requires re-authentication.
 * @param {any} error
 * @returns {boolean}
 */
export const isSessionInvalidError = (error) => {
  const normalized = normalizeAuth0Error(error);
  if (!normalized) return false;

  return SESSION_INVALID_ERROR_CODES.some((code) => normalized.includes(code));
};

/**
 * Determine whether the error indicates that the user is blocked in Auth0.
 * @param {any} error
 * @returns {boolean}
 */
export const isBlockedUserError = (error) => {
  const normalized = normalizeAuth0Error(error);
  if (!normalized) return false;

  return BLOCKED_USER_ERROR_CODES.some((code) => normalized.includes(code));
};

/**
 * Log the user out when the refresh token is no longer valid.
 * Clears cached Auth0 tokens and redirects to the login page.
 * @param {function|null} logoutFn
 */
export const logoutDueToSessionExpiry = async (logoutFn = null) => {
  clearAuth0Storage(new Set([USER_NOT_FOUND_FLAG]));

  const logout = logoutFn || logoutRef;
  const returnUrl = process.env.REACT_APP_AUTH0_RETURN_URL || window.location.origin;

  try {
    if (logout) {
      await logout({
        logoutParams: {
          returnTo: returnUrl,
        },
      });
    } else {
      window.location.href = returnUrl;
    }
  } catch (logoutError) {
    console.error('Logout due to session expiry failed, forcing navigation:', logoutError);
    window.location.href = returnUrl;
  }
};


/**
 * Get the access token for API calls
 * @returns {Promise<string>} The access token
 */
export const getAccessToken = async () => {
  if (!getAccessTokenSilentlyRef) {
    throw new Error("Auth0 token getter has not been registered.");
  }

  try {
    return await getAccessTokenSilentlyRef({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
    });
  } catch (error) {
    if (isSessionInvalidError(error)) {
      await logoutDueToSessionExpiry();
    }
    throw error;
  }
};

/**
 * Attempt to retrieve a fresh access token, forcing a refresh via Auth0.
 * @returns {Promise<string>} The refreshed access token
 */
export const refreshAccessToken = async () => {
  if (!getAccessTokenSilentlyRef) {
    throw new Error('Auth0 token getter has not been registered.');
  }

  if (!refreshPromise) {
    refreshPromise = getAccessTokenSilentlyRef({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      cacheMode: 'off',
    })
      .catch(async (error) => {
        if (isSessionInvalidError(error)) {
          await logoutDueToSessionExpiry();
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

/**
 * Display stored Auth0-related status messages (user-not-found, blocked-user) using the provided message API.
 * @param {{ error: Function }} messageApi
 */
export const showAuthStatusMessages = (messageApi) => {
  if (!messageApi) return;

  const showAndClear = (flag, callback) => {
    if (localStorage.getItem(flag) === 'true') {
      callback();
      localStorage.removeItem(flag);
    }
  };

  showAndClear(USER_NOT_FOUND_FLAG, () =>
    messageApi.error(
      'Your account is registered in Auth0 but not yet linked to the system. Please contact your administrator for access.',
      5
    )
  );

  showAndClear(BLOCKED_USER_FLAG, () =>
    messageApi.error(
      'Your account is blocked. Please contact your administrator.',
      8
    )
  );
};

